import type { TestSignalTriggerType } from "../g2-fleet-messages/test-signal-metadata";
import {
  TEST_SIGNAL_PATHS,
  type TestSignalLogCommandItem,
  type TestSignalLogItem,
  type TestSignalLogSignalItem,
  type TestSignalPath,
} from "../client-messages/test-signals";

/** One signal cell of the summary: the newest signal for a path and trigger type, or `null` if none in the window. */
export interface LatestTestSignal {
  readonly signalPath: TestSignalPath;
  readonly triggerType: TestSignalTriggerType;
  readonly signal: TestSignalLogSignalItem | null;
}

/** One command cell of the summary: the newest command sent for a path, or `null` if none in the window. */
export interface LatestTestCommand {
  readonly signalPath: TestSignalPath;
  readonly command: TestSignalLogCommandItem | null;
}

export interface LatestTestSignals {
  readonly signals: readonly LatestTestSignal[];
  readonly commands: readonly LatestTestCommand[];
}

/** Periodic first: the heartbeat is the question an operator usually opens the page with. */
const TRIGGER_TYPES = ["periodic", "manual"] as const satisfies readonly TestSignalTriggerType[];

/**
 * The summary: a signal cell per path and trigger type, and a command cell per
 * path.
 *
 * Combines the server's answer with the log rows already on the page. The
 * server covers rows the log has not paged to; the loaded rows cover a row that
 * landed after the summary was read — the log polls during a watch, the
 * summary does not. When both hold the same row, the loaded one wins, because
 * its outcome is at least as fresh.
 */
export function latestTestSignals(
  fetched: readonly TestSignalLogItem[],
  loaded: readonly TestSignalLogItem[],
): LatestTestSignals {
  // Loaded rows first, so they win a tie in `newest`.
  const candidates = [...loaded, ...fetched];
  const signalRows = candidates.filter((entry): entry is TestSignalLogSignalItem => entry.kind === "signal");
  const commandRows = candidates.filter((entry): entry is TestSignalLogCommandItem => entry.kind === "command");

  return {
    signals: TEST_SIGNAL_PATHS.flatMap((signalPath) =>
      TRIGGER_TYPES.map((triggerType) => ({
        signalPath,
        triggerType,
        signal: newest(signalRows.filter((row) => row.signalPath === signalPath && row.triggerType === triggerType)),
      })),
    ),
    commands: TEST_SIGNAL_PATHS.map((signalPath) => ({
      signalPath,
      command: newest(commandRows.filter((row) => row.signalPath === signalPath)),
    })),
  };
}

/** Newest by `occurredAt`; on a tie the earlier candidate wins, which is how loaded rows take precedence. */
function newest<T extends TestSignalLogItem>(candidates: readonly T[]): T | null {
  let best: T | null = null;
  for (const candidate of candidates) {
    if (best === null || candidate.occurredAt > best.occurredAt) best = candidate;
  }
  return best;
}
